import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Clock, User, ChevronLeft, ChevronRight,
  Trash2, CreditCard, Loader2
} from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, isToday } from 'date-fns';
import { useBooking } from '../context/BookingContext';
import { useAuth } from '../context/AuthContext';
import { getBusinessStaff } from '../services/firestore';
import { StaffMember } from '../types';

const Booking: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    bookingState, 
    removeService, 
    setSelectedDate, 
    setSelectedTime,
    setSelectedStaff,
    setCustomerInfo,
    getTotalPrice, 
    getTotalDuration 
  } = useBooking();

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [step, setStep] = useState<'date' | 'time' | 'info'>('date');
  const [loading] = useState(false);

  // Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const fetchStaff = useCallback(async () => {
    if (!businessId) return;
    const staffData = await getBusinessStaff(businessId);
    setStaff(staffData);
  }, [businessId]);

  useEffect(() => {
    if (businessId) {
      fetchStaff();
    }
  }, [businessId, fetchStaff]);

  useEffect(() => {
    // Check if services are selected
    if (bookingState.selectedServices.length === 0) {
      navigate(`/shop/${businessId}`);
    }
  }, [bookingState.selectedServices, businessId, navigate]);

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(currentWeekStart, i));
    }
    return days;
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setStep('time');
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setStep('info');
  };

  const handleProceedToPayment = () => {
    if (!bookingState.selectedDate || !bookingState.selectedTime) {
      return;
    }
    
    if (!user) {
      // Save current booking state and redirect to auth
      navigate('/auth', { state: { returnTo: `/checkout/${businessId}` } });
      return;
    }
    
    navigate(`/checkout/${businessId}`);
  };

  const weekDays = getWeekDays();

  return (
    <div className="min-h-screen bg-prim-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-16">
            <button
              onClick={() => {
                if (step === 'time') setStep('date');
                else if (step === 'info') setStep('time');
                else navigate(-1);
              }}
              className="p-2 -ml-2 hover:bg-gray-50 rounded-full"
            >
              <ArrowLeft size={20} className="text-prim-text" />
            </button>
            <h1 className="ml-4 text-lg font-semibold text-prim-text">
              {step === 'date' && 'Select Date'}
              {step === 'time' && 'Select Time'}
              {step === 'info' && 'Your Details'}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Business Info */}
        <div className="bg-white rounded-2xl p-4 border border-gray-100 mb-6">
          <div className="flex items-center">
            {bookingState.business?.businessImage ? (
              <img
                src={bookingState.business.businessImage}
                alt={bookingState.business.name}
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-primary-light flex items-center justify-center">
                <span className="text-xl font-bold text-primary">
                  {bookingState.business?.name.charAt(0)}
                </span>
              </div>
            )}
            <div className="ml-4">
              <h3 className="font-semibold text-prim-text">{bookingState.business?.name}</h3>
              <p className="text-sm text-gray-500">{bookingState.business?.address}</p>
            </div>
          </div>
        </div>

        {/* Selected Services Summary */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-6 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-prim-text">Selected Services</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {bookingState.selectedServices.map(({ service }) => (
              <div key={service.id} className="flex items-center justify-between p-4">
                <div className="flex-1">
                  <p className="font-medium text-prim-text">{service.name}</p>
                  <p className="text-sm text-gray-500">{service.duration} mins</p>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-prim-text mr-4">
                    £{service.price.toFixed(2)}
                  </span>
                  <button
                    onClick={() => removeService(service.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 bg-gray-50 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="font-bold text-xl text-prim-text">£{getTotalPrice().toFixed(2)}</p>
            </div>
            <p className="text-sm text-gray-500">
              <Clock size={14} className="inline mr-1" />
              {getTotalDuration()} mins
            </p>
          </div>
        </div>

        {/* Date Selection Step */}
        {step === 'date' && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
          >
            {/* Week Navigation */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <button
                onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
                className="p-2 hover:bg-gray-50 rounded-lg"
              >
                <ChevronLeft size={20} className="text-gray-600" />
              </button>
              <span className="font-medium text-prim-text">
                {format(weekDays[0], 'MMM d')} - {format(weekDays[6], 'MMM d, yyyy')}
              </span>
              <button
                onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
                className="p-2 hover:bg-gray-50 rounded-lg"
              >
                <ChevronRight size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-2 p-4">
              {weekDays.map((day) => {
                const isPast = day < new Date() && !isToday(day);
                const isSelected = bookingState.selectedDate && isSameDay(day, bookingState.selectedDate);
                
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => !isPast && handleDateSelect(day)}
                    disabled={isPast}
                    className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                      isPast 
                        ? 'opacity-40 cursor-not-allowed' 
                        : isSelected
                          ? 'bg-primary text-white'
                          : isToday(day)
                            ? 'bg-primary-light text-primary hover:bg-primary hover:text-white'
                            : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xs font-medium mb-1">
                      {format(day, 'EEE')}
                    </span>
                    <span className="text-lg font-semibold">
                      {format(day, 'd')}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Staff Selection */}
            {staff.length > 0 && (
              <div className="p-4 border-t border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  <User size={14} className="inline mr-1" />
                  Select Stylist (Optional)
                </h4>
                <div className="flex space-x-3 overflow-x-auto no-scrollbar">
                  <button
                    onClick={() => setSelectedStaff(null)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all flex-shrink-0 ${
                      !bookingState.selectedStaff
                        ? 'border-primary bg-primary-light'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <User size={20} className="text-gray-400" />
                    </div>
                    <span className="text-xs font-medium text-prim-text">Any</span>
                  </button>
                  {staff.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => setSelectedStaff(member)}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all flex-shrink-0 ${
                        bookingState.selectedStaff?.id === member.id
                          ? 'border-primary bg-primary-light'
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      {member.profileImage ? (
                        <img
                          src={member.profileImage}
                          alt={member.name}
                          className="w-12 h-12 rounded-full object-cover mb-2"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center mb-2">
                          <span className="text-white font-medium">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <span className="text-xs font-medium text-prim-text">{member.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Time Selection Step */}
        {step === 'time' && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
          >
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center text-primary">
                <Calendar size={18} className="mr-2" />
                <span className="font-medium">
                  {bookingState.selectedDate && format(bookingState.selectedDate, 'EEEE, MMMM d, yyyy')}
                </span>
              </div>
            </div>

            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Available Times</h4>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {timeSlots.map((time) => {
                  const isSelected = bookingState.selectedTime === time;
                  // Simulate some unavailable slots
                  const isAvailable = Math.random() > 0.2;
                  
                  return (
                    <button
                      key={time}
                      onClick={() => isAvailable && handleTimeSelect(time)}
                      disabled={!isAvailable}
                      className={`py-3 px-2 rounded-xl text-sm font-medium transition-all ${
                        !isAvailable
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : isSelected
                            ? 'bg-primary text-white'
                            : 'bg-gray-50 text-prim-text hover:bg-primary-light hover:text-primary'
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Customer Info Step */}
        {step === 'info' && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Appointment Summary */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="font-semibold text-prim-text mb-3">Appointment</h4>
              <div className="flex items-center text-gray-600 text-sm space-x-4">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2 text-primary" />
                  {bookingState.selectedDate && format(bookingState.selectedDate, 'EEE, MMM d')}
                </div>
                <div className="flex items-center">
                  <Clock size={16} className="mr-2 text-primary" />
                  {bookingState.selectedTime}
                </div>
                {bookingState.selectedStaff && (
                  <div className="flex items-center">
                    <User size={16} className="mr-2 text-primary" />
                    {bookingState.selectedStaff.name}
                  </div>
                )}
              </div>
            </div>

            {/* Customer Details Form */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="font-semibold text-prim-text mb-4">Your Details</h4>
              
              {user ? (
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center mr-3">
                      <User size={18} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-prim-text">{user.displayName || 'User'}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={bookingState.customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...bookingState.customerInfo, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={bookingState.customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...bookingState.customerInfo, email: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={bookingState.customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...bookingState.customerInfo, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      placeholder="+44 7700 000000"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Proceed Button */}
            <button
              onClick={handleProceedToPayment}
              disabled={loading}
              className="w-full py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CreditCard size={18} className="mr-2" />
                  Proceed to Payment • £{getTotalPrice().toFixed(2)}
                </>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Booking;

